<template>
  <div
    role="button"
    :class="classStyle"
    :style="{
      '--color': color,
      '--start-day': determineStartDay + '-start',
      '--end-day': determineEndDay + '-end',
    }"
    :draggable="draggable"
    @dragstart="startDrag"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot></slot>
    <pop-over v-if="($slots.popover || $scopedSlots.popover) && showPopOver" :bounds="bounds">
      <slot name="popover" />
    </pop-over>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import PopOver from '../../common/PopOver.vue'
import { defineComponent } from 'vue'
import { Prop } from 'vue/types/options'
import { mapGetters } from 'vuex'

export default defineComponent({
  name: 'CellBase',

  components: { PopOver },

  props: {
    start: {
      type: Object as Prop<moment.Moment>,
      required: true,
    },
    end: {
      type: Object as Prop<moment.Moment>,
      required: true,
    },
    color: {
      type: String,
      default: null,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    weekStart: {
      type: Object as Prop<moment.Moment>,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },

  data(): {
    hoverTimeoutId?: number
    bounds: { left: number; top: number; height: number } | null
    showPopOver: boolean
    documentListenersAttached: boolean
  } {
    return {
      hoverTimeoutId: undefined,
      bounds: null,
      showPopOver: false,
      documentListenersAttached: false,
    }
  },

  computed: {
    startDay() {
      return this.start.format('ddd').toLowerCase()
    },
    endDay() {
      return this.end.format('ddd').toLowerCase()
    },
    isCrossingFromPreviousWeek() {
      return !this.start.isSame(this.weekStart, 'week')
    },
    isCrossingToNextWeek() {
      return !this.end.isSame(this.weekStart, 'week')
    },
    determineStartDay() {
      return this.isCrossingFromPreviousWeek ? 'sun' : this.startDay
    },
    determineEndDay() {
      return this.isCrossingToNextWeek ? 'sat' : this.endDay
    },
    ...mapGetters('featureFlags', {
      weekViewDoubleHeightJobCard: 'weekViewDoubleHeightJobCard',
    }),
    classStyle() {
      const cssClass = [
        'cell',
        {
          'is-saving': this.isSaving,
          'is-crossing-from-previous-week': this.isCrossingFromPreviousWeek,
          'is-crossing-to-next-week': this.isCrossingToNextWeek,
        },
      ]
      if (this.weekViewDoubleHeightJobCard) {
        cssClass.push('double-height-cell')
        if (this.isCrossingFromPreviousWeek) {
          cssClass.push('is-crossing-from-previous-week-double-height')
        }
        if (this.isCrossingToNextWeek) {
          cssClass.push('is-crossing-to-next-week-dounle-height')
        }
      }
      return cssClass
    },
  },

  methods: {
    startDrag(event: DragEvent) {
      window.clearTimeout(this.hoverTimeoutId)
      this.hidePopOver()
      this.$emit('drag', event)
    },
    handleMouseEnter(event: MouseEvent) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
      this.bounds = { left: rect.x, top: rect.y, height: rect.height }

      this.hoverTimeoutId = window.setTimeout(() => {
        this.showPopOver = true
        this.attachDocumentListeners()
      }, 750)
    },
    handleMouseLeave(_: MouseEvent) {
      window.clearTimeout(this.hoverTimeoutId)
      this.hidePopOver()
    },
    handleDocumentMouseMove(event: MouseEvent) {
      if (!this.showPopOver) {
        return
      }

      const target = event.target as Node | null
      if (!target || !this.$el.contains(target)) {
        this.hidePopOver()
      }
    },
    handleDocumentScroll() {
      if (this.showPopOver) {
        this.hidePopOver()
      }
    },
    handleWindowBlur() {
      if (this.showPopOver) {
        this.hidePopOver()
      }
    },
    attachDocumentListeners() {
      if (this.documentListenersAttached) {
        return
      }

      this.documentListenersAttached = true
      document.addEventListener('mousemove', this.handleDocumentMouseMove)
      document.addEventListener('scroll', this.handleDocumentScroll, true)
      window.addEventListener('blur', this.handleWindowBlur)
    },
    detachDocumentListeners() {
      if (!this.documentListenersAttached) {
        return
      }

      document.removeEventListener('mousemove', this.handleDocumentMouseMove)
      document.removeEventListener('scroll', this.handleDocumentScroll, true)
      window.removeEventListener('blur', this.handleWindowBlur)
      this.documentListenersAttached = false
    },
    hidePopOver() {
      this.showPopOver = false
      this.detachDocumentListeners()
    },
  },

  beforeDestroy() {
    window.clearTimeout(this.hoverTimeoutId)
    this.detachDocumentListeners()
  },
})
</script>

<style scoped>
.cell {
  --color: var(--color-grey--lighter);
  --cell-background-color: var(--color-white);
  --offset-width: 18px;

  position: relative;
  display: flex;
  font-size: 12px;
  height: 26px;
  line-height: 1.5;
  align-items: center;
  padding: var(--space-smallest) var(--space-smaller);
  border: 1px solid;
  border-color: var(--color-grey--lighter);
  border-left: var(--space-smaller) solid var(--color);
  border-radius: var(--space-smaller);
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  background-color: var(--cell-background-color);
  grid-column-start: var(--start-day);
  grid-column-end: var(--end-day);
  margin: var(--space-smallest) var(--space-smaller);
  white-space: nowrap;
  z-index: var(--elevation-base);
  transition: all 200ms ease;
}

.double-height-cell {
  /* These CSS rules overwrites the corresponding rules in .cell */
  height: 48px;
  --offset-width: 32px;
  border-block-start: 2px solid;
  border-block-start-color: var(--color-grey--lighter);
  padding-block-start: 0;
}

.cell:hover {
  box-shadow: var(--shadow-low);
}

.is-crossing-from-previous-week {
  margin-left: var(--offset-width);
}

.is-crossing-to-next-week {
  margin-right: var(--offset-width);
}

.is-crossing-to-next-week::after,
.is-crossing-from-previous-week::before {
  content: '';
  position: absolute;
  top: -1px;
  display: block;
  width: calc(var(--offset-width) + 1px);
  height: calc(var(--offset-width) + 1px);
  background-color: inherit;
  border: var(--space-smallest) solid;
  border-color: inherit;
  z-index: -1;
}

.is-crossing-to-next-week-double-height::after,
.is-crossing-from-previous-week-double-height::before {
  border: var(--space-smaller) solid;
}

.is-crossing-to-next-week::after {
  left: 100%;
  border-left: none;
  border-bottom: none;
  transform: rotate(45deg);
  transform-origin: top left;
}

.is-crossing-from-previous-week::before {
  right: calc(100% + var(--space-smallest));
  border-color: var(--color);
  border-right: none;
  border-bottom: none;
  transform: rotate(-45deg);
  transform-origin: top right;
}

.is-saving {
  background-image: linear-gradient(
    135deg,
    rgba(0, 0, 0, 10%) 0%,
    rgba(0, 0, 0, 10%) 25%,
    transparent 25%,
    transparent 50%,
    rgba(0, 0, 0, 10%) 50%,
    rgba(0, 0, 0, 10%) 75%,
    transparent 75%,
    transparent 100%
  );
  background-size: 50px 50px;
  background-repeat: repeat;
  pointer-events: none;
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-name: animateStripes;
  animation-timing-function: linear;
}

@keyframes animateStripes {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 50px 50px;
  }
}
</style>
